// CruscottoView.jsx — amm-v3 (A1, ondata 28): PRIMA VISTA di AMM in React,
// il "Cruscotto scadenze" (pagina 'cruscotto'), flag-gated
// (shell_amm_v3_cruscotto, default OFF).
//
// Replica 1:1 renderCruscotto di admin-module.js r.1239-1344: stesso titolo,
// stessa riga "oggi …", stessa ricerca globale (debounce 300ms, dropdown per
// sorgente, ESC/click-fuori), stessi bottoni (Esporta CSV / Rigenera /
// + Scadenza), stesso banner promemoria (A3-077, dismiss in localStorage con
// chiave di stato), stessi 4 KPI, stessi chip filtro (aree + Solo scadute +
// responsabile), stessa lista "In scadenza" (dati riga campo-per-campo:
// giorno/mese, titolo, note/periodo, badge area/slittata/snooze/responsabile,
// importo €, pill urgenza) e stesso bulk "Segna pagate" (conferma informata,
// lock B6 per riga, esito onesto per-riga).
//
// La lista è il componente condiviso client/src/ui/DataTable.jsx (mandato A1)
// con celle custom; i BADGE e le FORMATTAZIONI sono DELEGATI alle primitive
// PURE del v1 (AMM.areaBadge/slittamentoBadge/promemoriaSnoozeBadge/
// responsabileBadge/urgency/eur) — unica fonte di verità, zero drift; l'HTML
// dei badge v1 è iniettato verbatim (dangerouslySetInnerHTML su stringhe
// prodotte dal v1 stesso, che già fa escaping con AMM.esc).
//
// CONFINI (dichiarati): modale scadenza = AMM.openEditor (v1); navigazione
// dei risultati di ricerca = AMM.ricercaGoto (v1); il refresh post-azione
// passa da AMM.renderPage(currentPage) — la STESSA via del v1, che sotto
// wrap ricarica questa vista.
//
// STATO (parità di persistenza col v1):
//  - filtri area/scadute/responsabile vivono SU AMM (filterArea/filterScadute/
//    filterResponsabile): CONDIVISI col v1 e col Calendario (A5-043: 'Solo
//    scadute' è un solo stato nel modulo) — flip di flag senza perdere filtri;
//  - la selezione bulk riparte pulita a ogni ricarica dati/cambio filtro
//    (parità A5-042: mai righe "selezionate" scomparse dalla vista).
//
// DEVIAZIONI DICHIARATE (v3 non bug-compatibile qui):
//  D-A1.1 doppio click su Rigenera/Segna pagate/Esporta = 1 richiesta
//         (guardia ref; il v1 non guarda Rigenera/Export).
//  D-A1.2 durante il refresh dati le righe presenti RESTANO visibili
//         (DataTable: "righe presenti ⇒ data sempre"); il v1 svuota tutto e
//         mostra 'Caricamento…' a ogni giro.
//  D-A1.3 errore su Rigenera dichiarato a toast (nel v1 resta muto).
//  D-A1.4 la lista è una TABELLA con intestazioni (DataTable, mandato) invece
//         delle righe libere v1; il bordo-urgenza a sinistra della riga v1 è
//         reso dal colore del giorno + pill (stessi dati, resa diversa).

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  AM_TEXTS, AM_NEW_TEXTS, AREE,
  filtraScadenze, responsabiliDistinti, normalizzaResponsabile, emptyMessage,
  totaleSelezionate, bulkIdsPayload, bulkBarraTesto, bulkConfirmTesto,
  bulkEsitoToast, bulkAlertTesto, dismissKey, bannerVisibile, kpiDefs,
  areaChipLabel, giornoMese, notaRiga, readCruscotto,
  apriEditor, apriNuovaScadenza, ricercaVai, refreshPagina,
  rigenera, bulkPagate, esportaCsv, ricerca,
} from './api.js';

// HTML prodotto dalle primitive v1 (già escapato da AMM.esc): iniettato verbatim
function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// ---- ricerca globale (replica r.1358-1476: debounce 300ms, min 2 char,
// stale-guard, gruppi per sorgente, ESC/click-fuori chiudono SEMPRE) ----
function RicercaGlobale({ win }) {
  const [drop, setDrop] = React.useState(null); // null=chiuso | {gruppi}|{empty:q}|{errore}
  const [q, setQ] = React.useState('');
  const wrapRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const chiudi = () => setDrop(null);

  React.useEffect(() => {
    const onDocClick = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) chiudi();
    };
    win.document.addEventListener('click', onDocClick);
    return () => win.document.removeEventListener('click', onDocClick);
  }, [win]);

  const esegui = async (testo) => {
    try {
      const data = await ricerca(win, testo);
      const cur = inputRef.current ? inputRef.current.value.trim() : '';
      if (testo !== cur) return; // risposta di una query ormai superata (parità r.1455)
      if (!data.gruppi || !data.gruppi.length) setDrop({ empty: testo });
      else setDrop({ gruppi: data.gruppi });
    } catch (e) {
      setDrop({ errore: e && e.message ? e.message : String(e) });
    }
  };

  const onInput = (ev) => {
    const v = ev.target.value;
    setQ(v);
    const t = v.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (t.length < 2) { chiudi(); return; }
    timerRef.current = setTimeout(() => esegui(t), 300);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Escape') { chiudi(); ev.target.blur(); }
  };

  return (
    <div id="amm-ricerca-wrap" className="relative flex-1 max-w-md" ref={wrapRef}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{'\u{1F50D}'}</span>
        <input
          id="amm-ricerca-input"
          ref={inputRef}
          type="text"
          autoComplete="off"
          placeholder={AM_TEXTS.searchPlaceholder}
          className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
          value={q}
          onChange={onInput}
          onKeyDown={onKeyDown}
          onFocus={() => { if (q.trim().length >= 2 && drop) setDrop({ ...drop }); }}
        />
      </div>
      {drop && (
        <div id="amm-ricerca-drop" className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl z-40 max-h-96 overflow-auto">
          {drop.errore !== undefined && (
            <div className="p-4 text-center text-sm text-rose-500">{AM_TEXTS.searchErrore + drop.errore}</div>
          )}
          {drop.empty !== undefined && (
            <div className="p-4 text-center text-sm text-slate-400">{AM_TEXTS.searchNessunRisultato + drop.empty + '"'}</div>
          )}
          {drop.gruppi && drop.gruppi.map((g) => (
            <div key={g.tipo} className="border-b last:border-0">
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">{g.label}</div>
              {g.risultati.map((r) => (
                <div
                  key={String(r.id)}
                  className="amm-ric-row px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-2"
                  onClick={() => { chiudi(); setQ(''); ricercaVai(win, g.tipo, r.id); }}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.titolo || ''}</div>
                    {r.sub ? <div className="text-xs text-slate-400 truncate">{r.sub}</div> : null}
                  </div>
                  {r.archiviato ? <span className="amm-badge flex-none" style={{ background: '#f1f5f9', color: '#64748b' }}>{AM_TEXTS.archiviatoBadge}</span> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CruscottoView({ win, tick }) {
  const A = win.AMM;
  const [dati, setDati] = React.useState({ loading: true, error: null, counts: {}, lista: [], promemoriaStato: null });
  const [reloadN, setReloadN] = React.useState(0);
  // filtri: stato React SPECCHIATO sui campi di AMM (write-through — condivisi
  // col v1 e col Calendario, parità A5-043/A4-098)
  const [f, setFState] = React.useState(() => ({
    area: A.filterArea || 'tutte',
    soloScadute: !!A.filterScadute,
    responsabile: A.filterResponsabile || '',
  }));
  const setF = (patch) => {
    const next = { ...f, ...patch };
    A.filterArea = next.area;
    A.filterScadute = next.soloScadute;
    A.filterResponsabile = next.responsabile;
    setFState(next);
  };
  const [sel, setSel] = React.useState(() => new Set());
  const [bannerGone, setBannerGone] = React.useState(false);
  const busyRef = React.useRef(false); // guardia SINCRONA anti doppio-click (D-A1.1)
  const [busyLabel, setBusyLabel] = React.useState(null);

  // dati: dashboard + promemoria.stato (stessa composizione del v1) a ogni
  // tick del wrapper (nav/rientro/refresh post-azione) o Riprova
  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readCruscotto(win).then(
      (r) => { if (vivo) { setDati({ loading: false, error: null, ...r }); setSel(new Set()); setBannerGone(false); } },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN]);

  // A4-098: se il responsabile filtrato è sparito dai dati, torna 'tutti'
  const responsabili = responsabiliDistinti(dati.lista);
  const respNorm = normalizzaResponsabile(f.responsabile, responsabili);
  React.useEffect(() => {
    if (respNorm !== f.responsabile) setF({ responsabile: respNorm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respNorm]);

  const shown = filtraScadenze(dati.lista, { ...f, responsabile: respNorm }, (s) => A.isScaduta(s));
  const selezionate = shown.filter((s) => sel.has(s.id));
  const nSel = selezionate.length;
  const totSel = totaleSelezionate(selezionate);

  // azione con guardia anti doppio-click + etichetta 'Attendere…' (parità busy() v1)
  const azione = (label, fn) => async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusyLabel(label);
    try { await fn(); } finally { busyRef.current = false; setBusyLabel(null); }
  };

  const onRigenera = azione(AM_TEXTS.rigenera, async () => {
    try {
      await rigenera(win);
      A.toast(AM_TEXTS.toastRigenerato);
      await refreshPagina(win);
    } catch (e) {
      // D-A1.3: nel v1 questo errore resta muto — qui si dichiara
      A.toast(AM_NEW_TEXTS.rigeneraFallito + (e && e.message ? e.message : e));
    }
  });

  const onExport = azione(AM_TEXTS.exportCsv, async () => {
    try { await esportaCsv(win); }
    catch (e) { A.toast(AM_TEXTS.toastExportFallito + (e && e.message ? e.message : e)); }
  });

  const onBulkPagate = azione(AM_TEXTS.segnaPagate, async () => {
    // conferma INFORMATA (v1 r.1203): N e totale € PRIMA di confermare
    if (!win.confirm(bulkConfirmTesto(nSel, A.eur(totSel)))) return;
    const ids = bulkIdsPayload(selezionate); // lock B6 per riga (parità r.1204)
    try {
      const esito = await bulkPagate(win, ids);
      setSel(new Set());
      A.toast(bulkEsitoToast(esito));
      await refreshPagina(win); // stessa via del v1 (renderPage(currentPage))
    } catch (e) {
      win.alert(bulkAlertTesto(e && e.message ? e.message : e)); // verbatim v1 r.1217
    }
  });

  const vaiAlProfilo = () => {
    // stessa via del v1 (r.1329-1332): click sulla voce sidebar se esiste,
    // altrimenti renderPage diretto
    const link = win.document.querySelector('.amm-link[data-page="profilo"]');
    if (link) link.click(); else A.renderPage('profilo');
  };

  const stato = dati.promemoriaStato;
  const banner = !bannerGone && bannerVisibile(stato, (k) => win.localStorage.getItem(k));

  const columns = [
    {
      key: '_sel',
      label: '',
      align: 'center',
      render: (s) => (A.scadPagabile(s)
        ? (
          <input
            type="checkbox"
            className="amm-bulk-chk"
            checked={sel.has(s.id)}
            onChange={() => {
              const next = new Set(sel);
              if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
              setSel(next);
            }}
          />
        )
        : null),
    },
    {
      key: '_data',
      label: AM_NEW_TEXTS.colData,
      align: 'center',
      render: (s) => {
        const u = A.urgency(s);
        const gm = giornoMese(s.data_scadenza);
        return (
          <div style={{ width: 46, textAlign: 'center', margin: '0 auto' }}>
            <div className="font-medium" style={{ color: u.b }}>{gm.giorno}</div>
            <div className="text-[9px] uppercase text-slate-400">{gm.mese}</div>
          </div>
        );
      },
    },
    {
      key: '_scadenza',
      label: AM_NEW_TEXTS.colScadenza,
      render: (s) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{s.titolo}</div>
            <div className="text-xs text-slate-400 truncate">{notaRiga(s)}</div>
          </div>
          <V1Html html={A.areaBadge(s.area)} />
          <V1Html html={A.slittamentoBadge(s)} />
          <V1Html html={A.promemoriaSnoozeBadge(s)} />
          <V1Html html={A.responsabileBadge(s)} />
        </div>
      ),
    },
    {
      key: '_importo',
      label: AM_NEW_TEXTS.colImporto,
      align: 'right',
      render: (s) => (
        <span className={'text-xs font-medium' + (s.importo_previsto == null ? ' text-slate-300' : '')}>{A.eur(s.importo_previsto)}</span>
      ),
    },
    {
      key: '_stato',
      label: AM_NEW_TEXTS.colStato,
      align: 'center',
      render: (s) => {
        const u = A.urgency(s);
        return (
          <span className="amm-badge" style={{ width: 74, textAlign: 'center', display: 'inline-block', background: u.pill[0], color: u.pill[1] }}>{u.pill[2]}</span>
        );
      },
    },
  ];

  const btnLabel = (label) => (busyLabel === label ? AM_NEW_TEXTS.attendere : label);

  return (
    <div data-amm-v3="cruscotto">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-xl font-semibold">{AM_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{AM_TEXTS.oggi + new Date().toLocaleDateString('it-IT')}</div>
        </div>
        <RicercaGlobale win={win} />
        <div className="flex gap-2 flex-none">
          <button id="amm-export-csv" type="button" disabled={!!busyLabel} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExport}>{btnLabel(AM_TEXTS.exportCsv)}</button>
          <button id="amm-rigen" type="button" disabled={!!busyLabel} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onRigenera}>{btnLabel(AM_TEXTS.rigenera)}</button>
          <button id="amm-scad-new" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white text-xs px-3 py-2 rounded font-medium" onClick={() => apriNuovaScadenza(win)}>{AM_TEXTS.nuovaScadenza}</button>
        </div>
      </div>

      {banner && (
        <div id="amm-promemoria-banner" className="mb-4 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
          <span>{AM_TEXTS.bannerPromemoria}</span>
          <span className="flex items-center gap-3 flex-none">
            <span id="amm-promemoria-vai" className="cursor-pointer underline font-semibold whitespace-nowrap" onClick={vaiAlProfilo}>{AM_TEXTS.bannerVaiProfilo + ' →'}</span>
            <span
              id="amm-promemoria-dismiss"
              className="cursor-pointer text-lg leading-none"
              title={AM_TEXTS.bannerChiudi}
              onClick={() => {
                try { win.localStorage.setItem(dismissKey(stato), '1'); } catch (e) { /* storage negato: si nasconde solo per questo render */ }
                setBannerGone(true);
              }}
            >{'×'}</span>
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {kpiDefs(dati.counts, (n) => A.eur(n)).map((k) => (
          <div key={k.label} className="bg-white border rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
            <div className={(k.small ? 'text-xl' : 'text-2xl') + ' font-semibold mt-1'} style={{ color: k.color }}>{k.val}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center mb-3">
        {AREE.map((a) => {
          const on = a === f.area;
          return (
            <span
              key={a}
              className={'amm-fchip cursor-pointer text-xs px-3 py-1.5 rounded-full border' + (on ? '' : ' bg-white text-slate-600 border-slate-200')}
              data-a={a}
              style={on ? { background: '#ecfeff', color: '#0e7490', borderColor: '#a5f3fc' } : undefined}
              onClick={() => setF({ area: a })}
            >{areaChipLabel(a)}</span>
          );
        })}
        <span className="text-slate-300">|</span>
        <span
          id="amm-chip-scadute"
          className={'amm-fchip cursor-pointer text-xs px-3 py-1.5 rounded-full border' + (f.soloScadute ? '' : ' bg-white text-slate-600 border-slate-200')}
          style={f.soloScadute ? { background: '#ffe4e6', color: '#9f1239', borderColor: '#fecdd3' } : undefined}
          onClick={() => setF({ soloScadute: !f.soloScadute })}
        >{AM_TEXTS.soloScadute}</span>
        {responsabili.length > 0 && (
          <>
            <span className="text-slate-300">|</span>
            <select
              id="amm-filtro-resp"
              className="text-xs px-2 py-1.5 rounded-full border bg-white text-slate-600 border-slate-200"
              value={respNorm}
              onChange={(ev) => setF({ responsabile: ev.target.value })}
            >
              <option value="">{AM_TEXTS.tuttiIResponsabili}</option>
              {responsabili.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </>
        )}
      </div>

      <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 ml-1">{AM_TEXTS.inScadenza}</div>

      <div id="amm-bulk-barra">
        {nSel > 0 && (
          <div className="flex items-center justify-between bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-2.5 mb-3">
            <span className="text-sm text-cyan-900">{bulkBarraTesto(nSel, A.eur(totSel))}</span>
            <div className="flex items-center gap-2">
              <button id="amm-bulk-annulla" type="button" className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5" onClick={() => setSel(new Set())}>{AM_TEXTS.annullaSelezione}</button>
              {A.isAdmin() && (
                <button id="amm-bulk-pagate" type="button" disabled={!!busyLabel} className="bg-cyan-700 hover:bg-cyan-600 text-white text-xs px-3 py-2 rounded font-medium" onClick={onBulkPagate}>
                  {busyLabel === AM_TEXTS.segnaPagate ? AM_NEW_TEXTS.attendere : AM_TEXTS.segnaPagate + nSel + ')'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* D-A1.5 (trovato dal collaudo): a ricarica fallita con righe presenti
          il DataTable tiene i dati (precedenza 'righe presenti ⇒ data') — qui
          l'errore si DICHIARA comunque, con Riprova accanto (mai silenzio) */}
      {dati.error && dati.lista.length > 0 && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" className="dt-retry" style={{ marginTop: 0 }} onClick={() => setReloadN((n) => n + 1)}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={shown}
        rowKey="id"
        loading={dati.loading}
        error={dati.error}
        onRetry={() => setReloadN((n) => n + 1)}
        emptyMessage={emptyMessage({ ...f, responsabile: respNorm })}
        onRowClick={(row) => apriEditor(win, row)}
      />
    </div>
  );
}

// Factory (pattern createProcV3Suppliers): install.js la registra nel registry
// del wrap di AMM.renderPage. render() è idempotente sul container #amm-page;
// unmount() libera il container (il v1 resta vivo per le altre pagine).
export function createAmmV3Cruscotto(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    // root nuovo se: primo giro, container sostituito/staccato, o container
    // SOVRASCRITTO dall'esterno a root vivo — rilevato col MARKER data-amm-v3
    // (probe a1-01 §4: root.render su container manomesso non ripristina la
    // vista e l'unmount lancia removeChild). Il caso reale: al reload
    // autenticato la continuazione async del renderPage v1 in volo scrive
    // innerHTML sopra il root (misurato in collaudo) — qui si GUARISCE:
    // container ripulito + root nuovo. Belt&braces oltre al seam (b).
    const manomesso = root && mountedEl === container && tick > 0
      && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato (misurato nel probe) */ } }
      // eventuali resti del v1 ('Caricamento…' o vista v1 al catch-up): il
      // root React parte da container pulito
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (nav, rientro, post-azione)
    root.render(<CruscottoView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
